import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // 🔴 SESIÓN 158 · AQUÍ HABÍA UN `&& !this.#isCartEmpty()` Y COSTABA 800 ms EN EL PEOR
    // MOMENTO POSIBLE. Con el cajón renderizado vacío no se abría hasta que resolvía el
    // `event.promise`, o sea tras DOS viajes de red (POST /cart/add.js + GET /cart.js).
    // Medido en la ficha del bolso a 390 px, del clic a la apertura:
    //     carrito VACÍO 870 ms   ·   carrito CON ALGO 54 ms
    // Y el carrito vacío es EL PRIMER AÑADIDO DE CUALQUIER CLIENTE.
    // Ahora abre siempre al instante; el contenido entra cuando llega, por el morph de siempre.
    // Mientras tanto el <dialog> lleva `cart-loading`, que el CSS de snippets/cart-drawer.liquid
    // usa para enseñar un ESQUELETO en vez del «Tu carrito está vacío» — sin eso, el cliente
    // leería que su carrito está vacío justo después de añadir, que es peor que el retraso.
    if (shouldAutoOpen && !sourceModal) {
      if (this.#isCartEmpty()) this.#dialog?.setAttribute('cart-loading', '');
      this.#themeDrawer?.open();
    }

    // 🔑 Sin promesa no habría quién quitara el esqueleto: se suelta aquí mismo.
    if (!event.promise) this.#dialog?.removeAttribute('cart-loading');

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            // En rAF, o sea DESPUÉS de que el morph haya metido el HTML nuevo: si se quitara en
            // el propio `then`, el esqueleto desaparecería un fotograma antes que los artículos.
            this.#dialog?.removeAttribute('cart-loading');
            this.#updateStickyState();
          });

        if (!shouldAutoOpen || detail?.didError) {
          // 🔑 Si abrimos «en seco» sobre un carrito vacío y el añadido FALLÓ, no se le puede
          // dejar al cliente un cajón vacío que no ha pedido: se cierra.
          if (shouldAutoOpen && detail?.didError && this.#isCartEmpty()) this.#themeDrawer?.close();
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        // 🔑 s158 · tercer camino de salida: si la promesa se rompe hay que soltar el esqueleto
        // igualmente, o el cajón se queda cargando para siempre.
        this.#dialog?.removeAttribute('cart-loading');
        if (shouldAutoOpen && this.#isCartEmpty()) this.#themeDrawer?.close();
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
